/**
 * Elements, Compounds & Mixtures Sorter — deterministic model and reviewed data.
 *
 * Classifies particle diagrams from their particle data, reads chemical formulas with the shared
 * parser in `chemicalEquation.ts`, and builds ball-and-stick models from atom data
 * (study-chemistry-tools-prd.md §8.1 `matter-classifier`; Year 7 `pure-mixture` mode from §8.2).
 */
import { parseCompound } from './chemicalEquation';
import { PERIODIC_ELEMENTS } from './periodicTableData';

export type ClassifierMode = 'pure-mixture' | 'classify' | 'formulas';
export type MatterClass = 'element' | 'compound' | 'mixture';

/** Stable identity used by reviewed CurriculumToolBinding records. */
export const MATTER_CLASSIFIER_TOOL_IDENTITY = {
  id: 'matter-classifier',
  toolKey: 'chemistry-matter-classifier',
  toolVersion: '1.0.0',
  computation: 'client-deterministic',
  evidencePolicy: 'none',
  masteryPolicy: 'not-assessed',
  curriculumPolicy: 'reviewed-binding-required',
} as const;

export const MATTER_CLASSIFIER_MODES = [
  { mode: 'pure-mixture', fromYear: 7, label: 'Year 7 · Pure or mixture' },
  { mode: 'classify', fromYear: 8, label: 'Year 8 · Classify' },
  { mode: 'formulas', fromYear: 9, label: 'Year 9 · Formulas' },
] as const satisfies readonly { mode: ClassifierMode; fromYear: number; label: string }[];

export const CLASSIFIER_REVIEW_STATUS = 'pending-teaching-review' as const;

export const MATTER_CLASSIFIER_SOURCES = [
  { id: 'iupac-gold-book', citation: 'IUPAC Compendium of Chemical Terminology (Gold Book) — definitions of chemical element, chemical compound, molecule and mixture.' },
  { id: 'iupac-red-book', citation: 'IUPAC Nomenclature of Inorganic Chemistry (Red Book, 2005) — formula conventions, including enclosing marks and subscripts.' },
  { id: 'textbook-structures', citation: 'Standard textbook structures: diatomic H₂, N₂, O₂; bent H₂O and O₃; linear CO₂; tetrahedral CH₄ (drawn flat); pyramidal NH₃ (drawn flat); face-centred cubic copper and the sodium chloride lattice (one layer shown).' },
] as const;

export const CONDITIONS_NOTE =
  'Particle diagrams show substances near room temperature and pressure (about 25 °C, 100 kPa). Particles are drawn flat, not to scale, and lattices show one small layer.';

export const MATTER_CLASS_LABELS: Record<MatterClass, string> = {
  element: 'Element',
  compound: 'Compound',
  mixture: 'Mixture',
};

export const ELEMENT_NAMES: Record<string, string> = {
  H: 'Hydrogen',
  He: 'Helium',
  C: 'Carbon',
  N: 'Nitrogen',
  O: 'Oxygen',
  Na: 'Sodium',
  Mg: 'Magnesium',
  Al: 'Aluminium',
  S: 'Sulfur',
  Cl: 'Chlorine',
  Ar: 'Argon',
  Ca: 'Calcium',
  Fe: 'Iron',
  Co: 'Cobalt',
  Cu: 'Copper',
};

export interface AtomSpec {
  element: string;
  /** Offset from the particle centre, in atom-diameter units. */
  x: number;
  y: number;
}

export interface BondSpec {
  from: number;
  to: number;
  order: 1 | 2 | 3;
}

export interface ParticleSpecies {
  id: string;
  name: string;
  formula: string;
  atoms: AtomSpec[];
  bonds: BondSpec[];
  /** Metal and ionic lattices are drawn as a grid instead of separate particles. */
  lattice?: {
    pattern: string[];
    /** Ionic lattices only: the ions and their fixed ratio, so feedback never implies molecules. */
    ionic?: { ions: string; ratio: string };
  };
}

function atom(element: string, x: number, y: number): AtomSpec {
  return { element, x, y };
}

function bond(from: number, to: number, order: 1 | 2 | 3 = 1): BondSpec {
  return { from, to, order };
}

const SPECIES_LIST: ParticleSpecies[] = [
  { id: 'He', name: 'helium atom', formula: 'He', atoms: [atom('He', 0, 0)], bonds: [] },
  { id: 'Ar', name: 'argon atom', formula: 'Ar', atoms: [atom('Ar', 0, 0)], bonds: [] },
  { id: 'H2', name: 'hydrogen molecule', formula: 'H2', atoms: [atom('H', -0.55, 0), atom('H', 0.55, 0)], bonds: [bond(0, 1)] },
  { id: 'N2', name: 'nitrogen molecule', formula: 'N2', atoms: [atom('N', -0.6, 0), atom('N', 0.6, 0)], bonds: [bond(0, 1, 3)] },
  { id: 'O2', name: 'oxygen molecule', formula: 'O2', atoms: [atom('O', -0.6, 0), atom('O', 0.6, 0)], bonds: [bond(0, 1, 2)] },
  { id: 'O3', name: 'ozone molecule', formula: 'O3', atoms: [atom('O', -1, 0.45), atom('O', 0, -0.25), atom('O', 1, 0.45)], bonds: [bond(0, 1, 2), bond(1, 2)] },
  { id: 'H2O', name: 'water molecule', formula: 'H2O', atoms: [atom('O', 0, -0.2), atom('H', -0.85, 0.45), atom('H', 0.85, 0.45)], bonds: [bond(0, 1), bond(0, 2)] },
  { id: 'H2O2', name: 'hydrogen peroxide molecule', formula: 'H2O2', atoms: [atom('H', -1.6, 0.6), atom('O', -0.6, 0), atom('O', 0.6, 0), atom('H', 1.6, -0.6)], bonds: [bond(0, 1), bond(1, 2), bond(2, 3)] },
  { id: 'CO', name: 'carbon monoxide molecule', formula: 'CO', atoms: [atom('C', -0.6, 0), atom('O', 0.6, 0)], bonds: [bond(0, 1, 3)] },
  { id: 'CO2', name: 'carbon dioxide molecule', formula: 'CO2', atoms: [atom('O', -1.2, 0), atom('C', 0, 0), atom('O', 1.2, 0)], bonds: [bond(0, 1, 2), bond(1, 2, 2)] },
  { id: 'CH4', name: 'methane molecule', formula: 'CH4', atoms: [atom('C', 0, 0), atom('H', 0, -1.1), atom('H', 1.1, 0), atom('H', 0, 1.1), atom('H', -1.1, 0)], bonds: [bond(0, 1), bond(0, 2), bond(0, 3), bond(0, 4)] },
  { id: 'NH3', name: 'ammonia molecule', formula: 'NH3', atoms: [atom('N', 0, 0), atom('H', -1, 0.65), atom('H', 1, 0.65), atom('H', 0, -1.1)], bonds: [bond(0, 1), bond(0, 2), bond(0, 3)] },
  { id: 'HCl', name: 'hydrogen chloride molecule', formula: 'HCl', atoms: [atom('H', -0.6, 0), atom('Cl', 0.65, 0)], bonds: [bond(0, 1)] },
  {
    id: 'C2H5OH',
    name: 'ethanol molecule',
    formula: 'C2H5OH',
    atoms: [
      atom('C', -1, 0), atom('C', 0.3, 0), atom('O', 1.6, 0), atom('H', 2.4, 0.6),
      atom('H', -1, -1.1), atom('H', -1, 1.1), atom('H', -2.1, 0), atom('H', 0.3, -1.1), atom('H', 0.3, 1.1),
    ],
    bonds: [bond(0, 1), bond(1, 2), bond(2, 3), bond(0, 4), bond(0, 5), bond(0, 6), bond(1, 7), bond(1, 8)],
  },
  { id: 'Cu-lattice', name: 'copper metal lattice', formula: 'Cu', atoms: [atom('Cu', 0, 0)], bonds: [], lattice: { pattern: ['Cu'] } },
  { id: 'NaCl-lattice', name: 'sodium chloride lattice', formula: 'NaCl', atoms: [atom('Na', 0, 0), atom('Cl', 1, 0)], bonds: [bond(0, 1)], lattice: { pattern: ['Na', 'Cl'], ionic: { ions: 'sodium and chloride ions (Na⁺ and Cl⁻)', ratio: '1 : 1' } } },
];

export const SPECIES: Record<string, ParticleSpecies> = Object.fromEntries(SPECIES_LIST.map((item) => [item.id, item]));

export function speciesElements(species: ParticleSpecies): string[] {
  return [...new Set(species.atoms.map((item) => item.element))];
}

/** Atom counts read from the model atoms (for a lattice, one formula unit). */
export function speciesAtomCounts(species: ParticleSpecies): Record<string, number> {
  return species.atoms.reduce<Record<string, number>>((counts, item) => {
    counts[item.element] = (counts[item.element] ?? 0) + 1;
    return counts;
  }, {});
}

// Classification

export interface DiagramComponent {
  speciesId: string;
  count: number;
}

export interface ClassifyCase {
  id: string;
  title: string;
  context: string;
  components: DiagramComponent[];
  answer: MatterClass;
  reviewStatus: typeof CLASSIFIER_REVIEW_STATUS;
}

function classifyCase(id: string, title: string, context: string, components: DiagramComponent[], answer: MatterClass): ClassifyCase {
  return { id, title, context, components, answer, reviewStatus: CLASSIFIER_REVIEW_STATUS };
}

function parts(...entries: Array<[string, number]>): DiagramComponent[] {
  return entries.map(([speciesId, count]) => ({ speciesId, count }));
}

export const CLASSIFY_CASES: ClassifyCase[] = [
  classifyCase('helium', 'Helium in a party balloon', 'A noble gas made of single, separate atoms.', parts(['He', 10]), 'element'),
  classifyCase('oxygen', 'Oxygen gas', 'The gas we breathe in, without the rest of the air.', parts(['O2', 7]), 'element'),
  classifyCase('nitrogen', 'Nitrogen gas', 'Pure nitrogen from a gas cylinder.', parts(['N2', 7]), 'element'),
  classifyCase('ozone', 'Ozone', 'A gas made of particles with three atoms each.', parts(['O3', 6]), 'element'),
  classifyCase('copper', 'Copper metal', 'A piece of pure copper wire.', parts(['Cu-lattice', 1]), 'element'),
  classifyCase('water', 'Pure water', 'Distilled water, shown as particles.', parts(['H2O', 7]), 'compound'),
  classifyCase('carbon-dioxide', 'Carbon dioxide', 'The gas from dry ice, with nothing else present.', parts(['CO2', 6]), 'compound'),
  classifyCase('salt', 'Sodium chloride (table salt)', 'A crystal of pure salt.', parts(['NaCl-lattice', 1]), 'compound'),
  classifyCase('methane', 'Methane', 'Pure natural-gas methane.', parts(['CH4', 5]), 'compound'),
  classifyCase('ammonia', 'Ammonia gas', 'Pure ammonia from a cylinder.', parts(['NH3', 6]), 'compound'),
  classifyCase('carbon-monoxide', 'Carbon monoxide', 'A toxic gas. Read the symbols carefully: C and O, not cobalt (Co).', parts(['CO', 7]), 'compound'),
  classifyCase('air', 'Dry air (simplified)', 'Mostly nitrogen with some oxygen.', parts(['N2', 6], ['O2', 2]), 'mixture'),
  classifyCase('hydrogen-oxygen', 'Hydrogen and oxygen before a spark', 'Two gases in one container that have not reacted yet.', parts(['H2', 6], ['O2', 3]), 'mixture'),
  classifyCase('noble-mix', 'Helium and argon', 'Two noble gases mixed in one cylinder.', parts(['He', 5], ['Ar', 4]), 'mixture'),
  classifyCase('humid-oxygen', 'Oxygen with water vapour', 'Damp oxygen gas: an element mixed with a compound.', parts(['O2', 4], ['H2O', 4]), 'mixture'),
  classifyCase('fizzy-gas', 'Carbon dioxide with water vapour', 'Gas above a fizzy drink: two compounds mixed together.', parts(['CO2', 4], ['H2O', 4]), 'mixture'),
];

export interface ClassifyFacts {
  oneKindOfAtom: boolean;
  differentAtomsJoined: boolean;
  moreThanOneKindOfParticle: boolean;
  elements: string[];
  particleFormulas: string[];
}

/** Only the particle content of a case is needed to read its facts or lay out its diagram. */
export type ParticleCase = Pick<ClassifyCase, 'id' | 'components'>;

export function caseFacts(item: Pick<ClassifyCase, 'components'>): ClassifyFacts | null {
  const species = item.components.map((component) => SPECIES[component.speciesId]);
  if (species.length === 0 || species.some((entry) => !entry) || item.components.some((component) => component.count < 1)) return null;
  const elements = [...new Set(species.flatMap(speciesElements))];
  return {
    oneKindOfAtom: elements.length === 1,
    differentAtomsJoined: species.some((entry) => speciesElements(entry).length > 1),
    moreThanOneKindOfParticle: new Set(species.map((entry) => entry.id)).size > 1,
    elements,
    particleFormulas: species.map((entry) => entry.formula),
  };
}

/** The class recomputed from particle data. Null when the diagram data is incomplete. */
export function classifyFromParticles(item: ClassifyCase): MatterClass | null {
  const facts = caseFacts(item);
  if (!facts) return null;
  if (facts.moreThanOneKindOfParticle) return 'mixture';
  return facts.oneKindOfAtom ? 'element' : 'compound';
}

export type EvidenceQuestion = 'oneKindOfAtom' | 'differentAtomsJoined' | 'moreThanOneKindOfParticle';

export const EVIDENCE_QUESTIONS: Array<{ id: EvidenceQuestion; label: string }> = [
  { id: 'oneKindOfAtom', label: 'Is there only one kind of atom?' },
  { id: 'differentAtomsJoined', label: 'Are atoms of different elements chemically joined?' },
  { id: 'moreThanOneKindOfParticle', label: 'Is there more than one kind of particle?' },
];

export function plainFormula(formula: string): string {
  const subscripts = '₀₁₂₃₄₅₆₇₈₉';
  return formula.replace(/\d/g, (digit) => subscripts[Number(digit)]);
}

function listFormulas(formulas: string[]): string {
  return [...new Set(formulas)].map(plainFormula).join(' and ');
}

function elementList(elements: string[]): string {
  const names = elements.map((symbol) => `${(ELEMENT_NAMES[symbol] ?? symbol).toLowerCase()} (${symbol})`);
  return names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names.join('');
}

export interface ClassifyResult {
  correct: boolean;
  expected: MatterClass;
  feedback: string;
}

/** Checks a classification and explains it from the particle facts. Null for incomplete data. */
export function checkClassification(item: ClassifyCase, choice: MatterClass): ClassifyResult | null {
  const facts = caseFacts(item);
  const expected = classifyFromParticles(item);
  if (!facts || !expected) return null;
  const formulas = listFormulas(facts.particleFormulas);
  const first = SPECIES[item.components[0].speciesId];
  const multiAtom = first.atoms.length > 1 || Boolean(first.lattice);
  const ionic = first.lattice?.ionic;
  let feedback: string;
  if (choice === expected) {
    if (expected === 'element') {
      feedback = multiAtom
        ? `Every atom is ${elementList(facts.elements)}. The atoms are joined, but they are all the same element, so this is an element.`
        : `Every particle is a single ${elementList(facts.elements)} atom. One kind of atom means an element.`;
    } else if (expected === 'compound' && ionic) {
      feedback = `The whole sample is one repeating lattice of ${ionic.ions} in a fixed ${ionic.ratio} ratio, chemically bonded throughout. Two elements combined in a fixed ratio make a compound.`;
    } else if (expected === 'compound') {
      feedback = `Every particle is the same, and each one contains ${elementList(facts.elements)} chemically joined in a fixed ratio (${formulas}). That is a compound.`;
    } else {
      feedback = `There is more than one kind of particle (${formulas}), and they are not joined to each other. That is a mixture.`;
    }
  } else if (expected === 'element') {
    feedback =
      choice === 'compound'
        ? multiAtom
          ? `The atoms in ${formulas} are joined, but they are all the same element: ${elementList(facts.elements)}. A compound needs atoms of different elements joined together.`
          : `Each particle is a single ${elementList(facts.elements)} atom. Nothing is joined, and there is only one element.`
        : `Every particle in the diagram is the same (${formulas}). A mixture needs more than one kind of particle.`;
  } else if (expected === 'compound' && ionic) {
    feedback =
      choice === 'element'
        ? `The lattice contains ${ionic.ions}. Two different elements are chemically bonded, so it is not an element.`
        : `There are two elements, but they are bonded throughout one repeating lattice of ${ionic.ions} in a fixed ${ionic.ratio} ratio. Nothing else is mixed in, so this is one pure substance, not a mixture.`;
  } else if (expected === 'compound') {
    feedback =
      choice === 'element'
        ? `Look inside one particle: ${formulas} contains ${elementList(facts.elements)}. Atoms of different elements are chemically joined, so it is not an element.`
        : `There are ${facts.elements.length} elements, but they are joined inside every particle in a fixed ratio (${formulas}). All the particles are identical, so this is one pure substance.`;
  } else {
    feedback = facts.differentAtomsJoined
      ? `Some particles are compounds, but the sample contains ${formulas} particles mixed together. More than one kind of particle makes a mixture.`
      : `Each kind of particle here is an element, but there are two kinds (${formulas}) that are not joined to each other. That makes a mixture of elements.`;
  }
  return { correct: choice === expected, expected, feedback };
}

export function checkEvidence(item: ClassifyCase, question: EvidenceQuestion, answer: boolean): boolean | null {
  const facts = caseFacts(item);
  return facts ? facts[question] === answer : null;
}

// Year 7: pure substance or mixture (no element/compound distinction required)

export type PurityClass = 'pure' | 'mixture';

export const PURITY_LABELS: Record<PurityClass, string> = {
  pure: 'Pure substance',
  mixture: 'Mixture',
};

export interface PurityCase {
  id: string;
  title: string;
  context: string;
  components: DiagramComponent[];
  answer: PurityClass;
  reviewStatus: typeof CLASSIFIER_REVIEW_STATUS;
}

function purityCase(id: string, title: string, context: string, components: DiagramComponent[], answer: PurityClass): PurityCase {
  return { id, title, context, components, answer, reviewStatus: CLASSIFIER_REVIEW_STATUS };
}

export const PURITY_CASES: PurityCase[] = [
  purityCase('pm-helium', 'Helium in a party balloon', 'Helium gas from a balloon cylinder.', parts(['He', 10]), 'pure'),
  purityCase('pm-distilled-water', 'Distilled water', 'Water that has been boiled and condensed to remove what was dissolved in it.', parts(['H2O', 7]), 'pure'),
  purityCase('pm-oxygen', 'Medical oxygen', 'Oxygen gas from a hospital cylinder.', parts(['O2', 7]), 'pure'),
  purityCase('pm-copper', 'Copper wire', 'The metal inside an electrical wire.', parts(['Cu-lattice', 1]), 'pure'),
  purityCase('pm-salt', 'A salt crystal', 'One crystal of pure table salt.', parts(['NaCl-lattice', 1]), 'pure'),
  purityCase('pm-carbon-dioxide', 'Gas from dry ice', 'The gas that comes off dry ice, with nothing else present.', parts(['CO2', 6]), 'pure'),
  purityCase('pm-ammonia', 'Ammonia gas', 'Ammonia gas from a sealed cylinder.', parts(['NH3', 6]), 'pure'),
  purityCase('pm-air', 'Dry air (simplified)', 'The air around you, with the water vapour removed.', parts(['N2', 6], ['O2', 2]), 'mixture'),
  purityCase('pm-oxygen-ozone', 'Oxygen with a little ozone', 'Every atom here is oxygen. Look at how the atoms are grouped.', parts(['O2', 5], ['O3', 3]), 'mixture'),
  purityCase('pm-fizzy-gas', 'Gas above a fizzy drink (simplified)', 'The gas in the top of an opened bottle of soft drink.', parts(['CO2', 4], ['H2O', 4]), 'mixture'),
  purityCase('pm-biogas', 'Biogas (simplified)', 'Gas from rotting food scraps in a biogas digester.', parts(['CH4', 5], ['CO2', 3]), 'mixture'),
  purityCase('pm-welding-gas', 'Welding gas', 'A cylinder of helium and argon used to shield a weld.', parts(['He', 5], ['Ar', 4]), 'mixture'),
];

export interface PurityFacts {
  /** Number of different kinds of particle in the diagram (a lattice counts as one). */
  particleKinds: number;
  particleFormulas: string[];
  lattice: boolean;
  oneKindOfAtom: boolean;
  differentAtomsJoined: boolean;
}

export function purityFacts(item: Pick<PurityCase, 'components'>): PurityFacts | null {
  const facts = caseFacts(item);
  if (!facts) return null;
  const species = item.components.map((component) => SPECIES[component.speciesId]);
  return {
    particleKinds: new Set(species.map((entry) => entry.id)).size,
    particleFormulas: [...new Set(facts.particleFormulas)],
    lattice: species.some((entry) => Boolean(entry.lattice)),
    oneKindOfAtom: facts.oneKindOfAtom,
    differentAtomsJoined: facts.differentAtomsJoined,
  };
}

/** Pure or mixture, recomputed from the particle data. Null when the diagram data is incomplete. */
export function purityFromParticles(item: Pick<PurityCase, 'components'>): PurityClass | null {
  const facts = purityFacts(item);
  if (!facts) return null;
  return facts.particleKinds > 1 ? 'mixture' : 'pure';
}

export interface PurityResult {
  correct: boolean;
  expected: PurityClass;
  feedback: string;
}

/** Checks a pure-or-mixture choice and explains it from the particles. Null for incomplete data. */
export function checkPurity(item: PurityCase, choice: PurityClass): PurityResult | null {
  const facts = purityFacts(item);
  const expected = purityFromParticles(item);
  if (!facts || !expected) return null;
  const formulas = listFormulas(facts.particleFormulas);
  const first = SPECIES[item.components[0].speciesId];
  const singleAtoms = first.atoms.length === 1 && !first.lattice;
  let feedback: string;
  if (expected === 'pure') {
    if (facts.lattice) {
      feedback = choice === 'pure'
        ? `The whole sample is one repeating pattern (${formulas}) with nothing else mixed in. One substance throughout means a pure substance.`
        : `This is one regular pattern (${formulas}) repeated everywhere. Nothing else is mixed in, so it is a pure substance.`;
    } else if (singleAtoms) {
      feedback = choice === 'pure'
        ? `Every particle is the same single ${formulas} atom. One kind of particle means a pure substance.`
        : `Every particle is a single ${formulas} atom, all the same. A mixture needs more than one kind of particle.`;
    } else if (facts.differentAtomsJoined) {
      feedback = choice === 'pure'
        ? `Every particle is the same ${formulas} particle. Different atoms are joined inside each particle, but the sample has only one kind of particle, so it is pure.`
        : `Each particle has more than one kind of atom, but the atoms are joined, and every particle is the same ${formulas}. Atoms joined inside one particle do not make a mixture.`;
    } else {
      feedback = choice === 'pure'
        ? `The atoms are joined in groups, and every group is the same ${formulas} particle. One kind of particle means a pure substance.`
        : `The atoms come in joined groups, but every group is the same ${formulas} particle. A mixture needs more than one kind of particle.`;
    }
  } else if (choice === 'mixture') {
    feedback = `There are ${facts.particleKinds} kinds of particle (${formulas}) side by side, not joined to each other. More than one kind of particle makes a mixture.`;
  } else {
    feedback = facts.oneKindOfAtom
      ? `Every atom is the same, but the atoms are grouped into different particles (${formulas}). Two kinds of particle make a mixture, even with only one kind of atom.`
      : `Look for different particles: this sample has ${formulas} particles side by side. They are not joined to each other, so the sample is a mixture.`;
  }
  return { correct: choice === expected, expected, feedback };
}

// Formulas

export interface FormulaCase {
  id: string;
  formula: string;
  name: string;
  /** Reviewed atom counts per element for one formula unit, in order of first appearance. */
  counts: Record<string, number>;
  reviewStatus: typeof CLASSIFIER_REVIEW_STATUS;
}

function formulaCase(id: string, formula: string, name: string, counts: Record<string, number>): FormulaCase {
  return { id, formula, name, counts, reviewStatus: CLASSIFIER_REVIEW_STATUS };
}

export const FORMULA_CASES: FormulaCase[] = [
  formulaCase('water', 'H2O', 'water', { H: 2, O: 1 }),
  formulaCase('carbon-dioxide', 'CO2', 'carbon dioxide', { C: 1, O: 2 }),
  formulaCase('ammonia', 'NH3', 'ammonia', { N: 1, H: 3 }),
  formulaCase('methane', 'CH4', 'methane', { C: 1, H: 4 }),
  formulaCase('sulfuric-acid', 'H2SO4', 'sulfuric acid', { H: 2, S: 1, O: 4 }),
  formulaCase('glucose', 'C6H12O6', 'glucose', { C: 6, H: 12, O: 6 }),
  formulaCase('ethanol', 'C2H5OH', 'ethanol', { C: 2, H: 6, O: 1 }),
  formulaCase('calcium-carbonate', 'CaCO3', 'calcium carbonate', { Ca: 1, C: 1, O: 3 }),
  formulaCase('iron-oxide', 'Fe2O3', 'iron(III) oxide', { Fe: 2, O: 3 }),
  formulaCase('copper-sulfate', 'CuSO4', 'copper(II) sulfate (anhydrous)', { Cu: 1, S: 1, O: 4 }),
  formulaCase('calcium-hydroxide', 'Ca(OH)2', 'calcium hydroxide', { Ca: 1, O: 2, H: 2 }),
  formulaCase('magnesium-nitrate', 'Mg(NO3)2', 'magnesium nitrate', { Mg: 1, N: 2, O: 6 }),
  formulaCase('aluminium-sulfate', 'Al2(SO4)3', 'aluminium sulfate', { Al: 2, S: 3, O: 12 }),
  formulaCase('ammonium-sulfate', '(NH4)2SO4', 'ammonium sulfate', { N: 2, H: 8, S: 1, O: 4 }),
];

export interface FormulaAnalysis {
  formula: string;
  counts: Record<string, number>;
  elements: string[];
  elementCount: number;
  totalAtoms: number;
}

/**
 * Reads one formula unit with the shared parser. Fails closed (returns an error) for coefficients,
 * state symbols, hydrate dots, charges and anything the parser rejects.
 */
export function analyseFormula(input: string): { analysis: FormulaAnalysis | null; error: string | null } {
  const formula = input.trim();
  if (!formula) return { analysis: null, error: 'Enter a formula, for example H2O or Ca(OH)2.' };
  if (/^\d/.test(formula)) return { analysis: null, error: 'Remove the number in front. A coefficient counts particles, not atoms in one formula unit.' };
  if (/[·•.*]/.test(formula)) return { analysis: null, error: 'Hydrate formulas (with a dot) are not supported in this tool yet.' };
  if (/[+\-⁺⁻]/.test(formula)) return { analysis: null, error: 'Ion charges are not supported here. Enter a neutral formula.' };
  if (/\((aq|s|l|g)\)$/i.test(formula)) return { analysis: null, error: 'Remove the state symbol, for example (aq) or (s).' };
  const parsed = parseCompound(formula);
  if (!parsed.compound || parsed.error) return { analysis: null, error: parsed.error?.message ?? 'This formula could not be read.' };
  const counts = parsed.compound.atoms;
  const elements = Object.keys(counts);
  return {
    analysis: {
      formula,
      counts,
      elements,
      elementCount: elements.length,
      totalAtoms: Object.values(counts).reduce((sum, count) => sum + count, 0),
    },
    error: null,
  };
}

export interface FormulaMassTerm {
  element: string;
  count: number;
  /** Standard atomic weight as written in the periodic-table dataset, for example "12.011". */
  arText: string;
  product: number;
}

export interface FormulaMassWorking {
  terms: FormulaMassTerm[];
  total: number;
  /** For example "Mr = 6 × 12.011 + 12 × 1.008 + 6 × 15.999 = 72.066 + 12.096 + 95.994 = 180.16". */
  working: string;
}

const ATOMIC_MASS_TEXT = new Map(PERIODIC_ELEMENTS.map((element) => [element.symbol, element.atomicMass]));

/** Rounds to a fixed number of decimals so floating-point noise never reaches the learner. */
function roundMass(value: number, digits: number): number {
  const factor = 10 ** digits;
  return Math.round(Number((value * factor).toPrecision(12))) / factor;
}

/**
 * Relative formula mass Mr = Σ(count × Ar) from the periodic-table dataset (CT-INTERACT worked
 * step). Fails closed for a symbol that is not an element, and for an element whose dataset entry
 * has no standard atomic weight (radioactive elements listed as "[98] (Radioactive)").
 */
export function relativeFormulaMass(analysis: Pick<FormulaAnalysis, 'counts' | 'elements'>): {
  mass: FormulaMassWorking | null;
  error: string | null;
} {
  const terms: FormulaMassTerm[] = [];
  for (const element of analysis.elements) {
    const arText = ATOMIC_MASS_TEXT.get(element);
    if (arText === undefined) return { mass: null, error: `${element} is not an element symbol, so Mr cannot be worked out.` };
    if (!/^\d+(\.\d+)?$/.test(arText)) {
      return { mass: null, error: `${element} has no standard atomic weight in the reviewed table, so Mr is not worked out.` };
    }
    const count = analysis.counts[element];
    terms.push({ element, count, arText, product: roundMass(count * Number(arText), 3) });
  }
  if (!terms.length) return { mass: null, error: 'Enter a formula to work out Mr.' };
  const total = roundMass(terms.reduce((sum, term) => sum + term.product, 0), 2);
  const substituted = terms.map((term) => `${term.count} × ${term.arText}`).join(' + ');
  const products = terms.map((term) => String(term.product)).join(' + ');
  const working =
    terms.length === 1 && terms[0].count === 1
      ? `Mr = ${substituted} = ${total.toFixed(2)}`
      : `Mr = ${substituted} = ${products} = ${total.toFixed(2)}`;
  return { mass: { terms, total, working }, error: null };
}

/** Counts if every bracket multiplier were ignored — a common misreading. */
export function countsIgnoringBracketMultipliers(formula: string): Record<string, number> | null {
  const stripped = formula.replace(/\)\d+/g, ')').replace(/[()]/g, '');
  return analyseFormula(stripped).analysis?.counts ?? null;
}

/** Number of element symbols written in the formula, counting repeats (H in C2H5OH twice). */
export function symbolOccurrences(formula: string): number {
  return formula.match(/[A-Z][a-z]?/g)?.length ?? 0;
}

export interface CountCheck {
  correct: boolean;
  feedback: string;
}

export function checkElementCount(formula: string, answer: number): CountCheck | null {
  const { analysis } = analyseFormula(formula);
  if (!analysis || !Number.isInteger(answer) || answer < 0) return null;
  if (answer === analysis.elementCount) {
    return { correct: true, feedback: `Yes: ${elementList(analysis.elements)} — ${analysis.elementCount} different elements.` };
  }
  if (answer === symbolOccurrences(formula)) {
    return { correct: false, feedback: 'You counted a symbol each time it appears. A repeated symbol is still the same element, so count each element once.' };
  }
  if (answer === analysis.totalAtoms) {
    return { correct: false, feedback: 'That is the number of atoms. The question asks how many different kinds of atom (elements) there are.' };
  }
  return { correct: false, feedback: 'List each capital letter that starts a new symbol, then count the different symbols once each.' };
}

export function checkTotalAtoms(formula: string, answer: number): CountCheck | null {
  const { analysis } = analyseFormula(formula);
  if (!analysis || !Number.isInteger(answer) || answer < 0) return null;
  if (answer === analysis.totalAtoms) return { correct: true, feedback: `Yes: ${analysis.totalAtoms} atoms in one formula unit.` };
  const naive = countsIgnoringBracketMultipliers(formula);
  const naiveTotal = naive ? Object.values(naive).reduce((sum, count) => sum + count, 0) : null;
  if (naiveTotal !== null && naiveTotal !== analysis.totalAtoms && answer === naiveTotal) {
    return { correct: false, feedback: 'The number after a closing bracket multiplies everything inside the brackets. Count the bracket group again.' };
  }
  if (answer === symbolOccurrences(formula)) {
    return { correct: false, feedback: 'You counted each symbol once. A subscript tells you how many of that atom there are, for example H₂ means two H atoms.' };
  }
  return { correct: false, feedback: 'Add the subscript of every element (no subscript means 1), and multiply anything inside brackets by the number after the bracket.' };
}

export function checkAtomCount(formula: string, element: string, answer: number): CountCheck | null {
  const { analysis } = analyseFormula(formula);
  if (!analysis || !(element in analysis.counts) || !Number.isInteger(answer) || answer < 0) return null;
  const expected = analysis.counts[element];
  const name = ELEMENT_NAMES[element] ?? element;
  if (answer === expected) return { correct: true, feedback: `${name}: ${expected}.` };
  const naive = countsIgnoringBracketMultipliers(formula)?.[element];
  if (naive !== undefined && naive !== expected && answer === naive) {
    return { correct: false, feedback: `${name} sits inside brackets. Multiply its count by the number after the closing bracket.` };
  }
  return { correct: false, feedback: `Find every ${element} in the formula and add their subscripts (no subscript means 1).` };
}

// Ball-and-stick matching

export interface MatchCase {
  id: string;
  speciesId: string;
  /** Formula options; the correct one is the species formula. */
  options: string[];
  hint: string;
  reviewStatus: typeof CLASSIFIER_REVIEW_STATUS;
}

function matchCase(id: string, speciesId: string, options: string[], hint: string): MatchCase {
  return { id, speciesId, options, hint, reviewStatus: CLASSIFIER_REVIEW_STATUS };
}

export const MATCH_CASES: MatchCase[] = [
  matchCase('water', 'H2O', ['H2O', 'H2O2', 'HO2', 'H3O'], 'Count the oxygen atoms: one, joined to two hydrogens.'),
  matchCase('peroxide', 'H2O2', ['H2O', 'H2O2', 'HO', 'H4O2'], 'There are two O atoms joined to each other, each with one H.'),
  matchCase('carbon-dioxide', 'CO2', ['CO', 'CO2', 'C2O', 'Co'], 'One carbon in the middle, one oxygen on each side.'),
  matchCase('carbon-monoxide', 'CO', ['CO', 'CO2', 'Co', 'C2O2'], 'Co is cobalt, one element. CO is one carbon joined to one oxygen.'),
  matchCase('methane', 'CH4', ['CH4', 'C4H', 'CH3', 'C2H4'], 'The subscript belongs to the atom before it: four hydrogens, one carbon.'),
  matchCase('ammonia', 'NH3', ['NH3', 'N3H', 'NH4', 'N2H6'], 'Count the H atoms joined to the single N.'),
  matchCase('ozone', 'O3', ['O2', 'O3', 'O', 'O6'], 'All the atoms are oxygen. How many are joined in one particle?'),
  matchCase('hydrogen-chloride', 'HCl', ['HCl', 'HC', 'H2Cl', 'HCl2'], 'Cl is chlorine. One hydrogen joined to one chlorine.'),
  matchCase('ethanol', 'C2H5OH', ['C2H5OH', 'C2H6', 'CH3OH', 'C2H4O2'], 'Count carbons, then hydrogens (one is on the oxygen), then oxygens.'),
  matchCase('nitrogen', 'N2', ['N2', 'N', 'N3', 'NO'], 'Two nitrogen atoms joined by a triple bond.'),
];

export function checkMatch(item: MatchCase, formula: string): { correct: boolean; expected: string; feedback: string } | null {
  const species = SPECIES[item.speciesId];
  if (!species || !item.options.includes(formula)) return null;
  const correct = formula === species.formula;
  if (correct) return { correct, expected: species.formula, feedback: `Yes — the model is a ${species.name}, ${plainFormula(species.formula)}.` };
  const chosen = analyseFormula(formula).analysis;
  const modelCounts = speciesAtomCounts(species);
  const diffs = chosen
    ? [...new Set([...Object.keys(modelCounts), ...Object.keys(chosen.counts)])]
        .filter((element) => (modelCounts[element] ?? 0) !== (chosen.counts[element] ?? 0))
        .map((element) => `${ELEMENT_NAMES[element] ?? element}: model has ${modelCounts[element] ?? 0}, ${plainFormula(formula)} has ${chosen.counts[element] ?? 0}`)
    : [];
  return { correct, expected: species.formula, feedback: `${diffs.join('; ')}. ${item.hint}` };
}

// Diagram layout (positions generated from the particle data)

export const DIAGRAM_BOX = { width: 240, height: 160 } as const;
const ATOM_UNIT = 14;
const LATTICE_COLUMNS = 8;
const LATTICE_ROWS = 5;
const LATTICE_SPACING = 24;

export interface PlacedAtom {
  key: string;
  element: string;
  x: number;
  y: number;
  particle: number;
}

export interface PlacedBond {
  key: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  order: 1 | 2 | 3;
}

function seededRandom(seed: number): () => number {
  let value = seed >>> 0;
  return () => {
    value = (value + 0x6d2b79f5) >>> 0;
    let mixed = Math.imul(value ^ (value >>> 15), value | 1);
    mixed ^= mixed + Math.imul(mixed ^ (mixed >>> 7), mixed | 61);
    return ((mixed ^ (mixed >>> 14)) >>> 0) / 4294967296;
  };
}

function hashId(id: string): number {
  return [...id].reduce((sum, character) => (sum * 31 + character.charCodeAt(0)) % 2147483647, 7);
}

/** Places a lattice grid or scattered particles inside the diagram box. */
export function layoutDiagram(item: ParticleCase): { atoms: PlacedAtom[]; bonds: PlacedBond[] } {
  const atoms: PlacedAtom[] = [];
  const bonds: PlacedBond[] = [];
  const first = SPECIES[item.components[0]?.speciesId];
  if (first?.lattice) {
    const pattern = first.lattice.pattern;
    const left = (DIAGRAM_BOX.width - (LATTICE_COLUMNS - 1) * LATTICE_SPACING) / 2;
    const top = (DIAGRAM_BOX.height - (LATTICE_ROWS - 1) * LATTICE_SPACING) / 2;
    for (let row = 0; row < LATTICE_ROWS; row += 1) {
      for (let column = 0; column < LATTICE_COLUMNS; column += 1) {
        atoms.push({ key: `${row}-${column}`, element: pattern[(row + column) % pattern.length], x: left + column * LATTICE_SPACING, y: top + row * LATTICE_SPACING, particle: 0 });
      }
    }
    return { atoms, bonds };
  }
  const particles = item.components.flatMap((component) => Array.from({ length: component.count }, () => SPECIES[component.speciesId]));
  const random = seededRandom(hashId(item.id));
  const columns = Math.ceil(Math.sqrt(particles.length * 1.5));
  const rows = Math.ceil(particles.length / columns);
  const cellWidth = DIAGRAM_BOX.width / columns;
  const cellHeight = DIAGRAM_BOX.height / rows;
  // Shuffle cell order so mixtures look mixed rather than sorted.
  const cells = Array.from({ length: columns * rows }, (_, index) => index);
  for (let index = cells.length - 1; index > 0; index -= 1) {
    const swap = Math.floor(random() * (index + 1));
    [cells[index], cells[swap]] = [cells[swap], cells[index]];
  }
  particles.forEach((species, index) => {
    if (!species) return;
    const cell = cells[index];
    const centreX = (cell % columns) * cellWidth + cellWidth / 2 + (random() - 0.5) * cellWidth * 0.2;
    const centreY = Math.floor(cell / columns) * cellHeight + cellHeight / 2 + (random() - 0.5) * cellHeight * 0.2;
    const angle = random() * Math.PI * 2;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const placed = species.atoms.map((spec) => ({
      x: centreX + (spec.x * cos - spec.y * sin) * ATOM_UNIT,
      y: centreY + (spec.x * sin + spec.y * cos) * ATOM_UNIT,
    }));
    species.bonds.forEach((spec, bondIndex) => {
      bonds.push({ key: `${index}-b${bondIndex}`, x1: placed[spec.from].x, y1: placed[spec.from].y, x2: placed[spec.to].x, y2: placed[spec.to].y, order: spec.order });
    });
    species.atoms.forEach((spec, atomIndex) => {
      atoms.push({ key: `${index}-a${atomIndex}`, element: spec.element, x: placed[atomIndex].x, y: placed[atomIndex].y, particle: index });
    });
  });
  return { atoms, bonds };
}

/** One molecule centred in its own box, for ball-and-stick matching. */
export function layoutMolecule(species: ParticleSpecies, unit = 26): { atoms: PlacedAtom[]; bonds: PlacedBond[] } {
  const centreX = DIAGRAM_BOX.width / 2;
  const centreY = DIAGRAM_BOX.height / 2;
  const atoms = species.atoms.map((spec, index) => ({ key: `a${index}`, element: spec.element, x: centreX + spec.x * unit, y: centreY + spec.y * unit, particle: 0 }));
  const bonds = species.bonds.map((spec, index) => ({ key: `b${index}`, x1: atoms[spec.from].x, y1: atoms[spec.from].y, x2: atoms[spec.to].x, y2: atoms[spec.to].y, order: spec.order }));
  return { atoms, bonds };
}
